import type {
  GuestSessionExchangeBody,
  PreLoginRedirect,
  RedirectURI,
  RedirectURIOptional,
  WebsocketConnectionParams,
  WorkOSAuthCheck,
} from '@aline/common';
import {
  clearGuestSessionQueryKey,
  csrfCookieName,
  csrfMismatchMessage,
  CSRFResponseSchema,
  csrfTokenMissingMessage,
  guestExchangeQueryKey,
  GuestSessionExchangeResponseSchema,
  guestSessionHeaderName,
  guestSessionStorageKey,
  RedirectURIOptionalSchema,
  RedirectURISchema,
  wosSessionCookieName,
} from '@aline/common';
import { routes } from '@aline/routes';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import Cookie from 'js-cookie';
import { clearExternalSession } from 'src/layouts/external-session';
import { clearSentryUser } from 'src/sentry';

import { useCurrentUserQuery } from './gql';
import { doNothing, postFetch } from './helpers';

/**
 * Rewrites the address bar in place. Pass the router's navigate: a bare history.replaceState
 * leaves the router on the old URL, and its next navigation writes the stripped params back.
 */
export type ReplaceUrl = (url: URL) => void;

export async function fetchCSRFToken(): Promise<string> {
  const res = await fetch(routes.ApiCSRF);
  const { csrfToken } = CSRFResponseSchema.parse(await res.json());
  return csrfToken;
}

export async function findOrCreateCSRFToken(): Promise<string> {
  const token = getCSRFToken();
  if (token) return token;

  return fetchCSRFToken();
}

export function getCSRFToken(): string {
  return Cookie.get(csrfCookieName) ?? '';
}

/**
 * Lusca-protected POST: use the cached `_csrf` cookie, and on mismatch fetch a
 * fresh token once and retry. Lusca rejects before the route handler runs, so
 * the retry is not a double-mutation.
 */
export async function postWithCsrf(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && Boolean(init.body))
    headers.set('Content-Type', 'application/json');

  headers.set('x-csrf-token', await findOrCreateCSRFToken());

  const res = await fetch(url, { ...init, headers, method: 'POST' });
  if (!(await getIsCsrfMismatch(res))) return res;

  const retryHeaders = new Headers(headers);
  retryHeaders.set('x-csrf-token', await fetchCSRFToken());
  return fetch(url, { ...init, headers: retryHeaders, method: 'POST' });
}

/**
 * Detect Lusca CSRF failures, including CloudFront rewriting origin 403s to
 * `200 text/html` (SPA index.html) so a bare status===403 check is not enough.
 */
async function getIsCsrfMismatch(res: Response): Promise<boolean> {
  const contentType = res.headers.get('content-type') ?? '';
  if (res.status === 200 && contentType.includes('text/html')) return true;

  if (res.status !== 403 && res.status !== 500) return false;

  const text = await res.clone().text();
  return text.includes(csrfMismatchMessage) || text.includes(csrfTokenMissingMessage);
}

export function getGuestSessionToken(): string | null {
  return sessionStorage.getItem(guestSessionStorageKey);
}

export function clearGuestSessionToken(): void {
  sessionStorage.removeItem(guestSessionStorageKey);
}

/**
 * Ends the Redis-backed guest session for this tab and clears sessionStorage.
 * Does not touch the WorkOS cookie.
 */
export async function endGuestSession(): Promise<void> {
  const token = getGuestSessionToken();
  if (!token) return;

  await postWithCsrf(routes.ApiGuestSessionEnd, {
    headers: {
      'Content-Type': 'application/json',
      [guestSessionHeaderName]: token,
    },
  });
  clearGuestSessionToken();
}

/**
 * AuthKit login redirects with ?clearGuestSession=1 so only this tab drops any
 * guest identity and destroys the Redis session. Cookies cannot be tab-scoped.
 */
export async function consumeClearGuestSessionFromUrl(replaceUrl: ReplaceUrl): Promise<void> {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(clearGuestSessionQueryKey)) return;

  url.searchParams.delete(clearGuestSessionQueryKey);
  replaceUrl(url);

  clearExternalSession();
  await endGuestSession();
}

/**
 * Redeems a short-lived magic-link exchange code into tab-scoped sessionStorage
 * and strips it from the address bar only after success. Failures leave the
 * one-time code in the URL so refresh can retry while Redis still holds it.
 */
export async function consumeGuestExchangeFromUrl(replaceUrl: ReplaceUrl): Promise<void> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get(guestExchangeQueryKey);
  if (!code) return;

  try {
    const res = await postWithCsrf(routes.ApiGuestSessionExchange, {
      body: JSON.stringify({ code } satisfies GuestSessionExchangeBody),
    });
    if (!res.ok) return;

    const { guestSessionToken } = GuestSessionExchangeResponseSchema.parse(await res.json());
    sessionStorage.setItem(guestSessionStorageKey, guestSessionToken);

    url.searchParams.delete(guestExchangeQueryKey);
    replaceUrl(url);
  } catch {
    // check-auth / pre-login handles the unauthenticated case. Keep guestCode
    // in the URL so a refresh can retry a still-valid Redis exchange code.
  }
}

export function getWebsocketConnectionParams(): WebsocketConnectionParams | undefined {
  const guestSession = getGuestSessionToken();
  if (guestSession) return { __typename: 'GuestSessionToken', token: guestSession };

  const wosSessionCookie = Cookie.get(wosSessionCookieName);
  if (wosSessionCookie) return { __typename: 'WorkOSSessionCookie', cookie: wosSessionCookie };

  return undefined;
}

/**
 * Redirects to WorkOS login.
 */
export async function redirectToLogin(): Promise<void> {
  // Redirect to WorkOS login.
  const { redirectUri } = await getLoginRedirectURI();
  window.location.href = redirectUri.route;
}

/**
 * Redirects to WorkOS signup.
 */
export async function redirectToSignup(): Promise<void> {
  // Redirect to WorkOS signup.
  const { redirectUri } = await getSignupRedirectURI();
  window.location.href = redirectUri.route;
}

/**
 * Checks if the user is authenticated by checking the guest session or WorkOS session.
 */
export async function getAuthRedirectURI(
  redirectPath: string,
  replaceUrl: ReplaceUrl
): Promise<RedirectURIOptional> {
  await consumeClearGuestSessionFromUrl(replaceUrl);
  await consumeGuestExchangeFromUrl(replaceUrl);

  const guestToken = getGuestSessionToken();
  /**
   * Always fetch CSRF fresh — a cached `_csrf` cookie can desync from the
   * httpOnly `csrf-session`, and CloudFront rewrites origin 403s to SPA HTML
   * so a stale-token POST looks like a successful 200 with a 9KB HTML body.
   */
  const csrfToken = await fetchCSRFToken();
  const res = await fetch(routes.ApiCheckAuth, {
    body: JSON.stringify({ redirectPath } satisfies WorkOSAuthCheck),
    headers: {
      'Content-Type': 'application/json',
      'x-csrf-token': csrfToken,
      ...(guestToken ? { [guestSessionHeaderName]: guestToken } : {}),
    },
    method: 'POST',
  });
  return RedirectURIOptionalSchema.parse(await res.json());
}

/**
 * Given the user lands on the pre-login page, we need to redirect them to the
 * login page with the correct sign-in method.
 *  1) If they are magic-link users, redirect to the login page with a magic link.
 *  2) If they are username/password users, redirect to the login page with username/password.
 */
export async function getPreLoginRedirectURI(
  email: string,
  redirectPath: string
): Promise<RedirectURI> {
  const res = await postFetch<PreLoginRedirect>(routes.ApiPreLoginRedirect, {
    email,
    redirectPath,
  });

  return RedirectURISchema.parse(await res.json());
}

/**
 * Gets the login redirect URI from WorkOS.
 */
export async function getLoginRedirectURI(): Promise<RedirectURI> {
  const res = await fetch(routes.ApiLoginRedirect);
  return RedirectURISchema.parse(await res.json());
}

/**
 * Gets the signup redirect URI from WorkOS.
 */
export async function getSignupRedirectURI(): Promise<RedirectURI> {
  const res = await fetch(routes.ApiSignupRedirect);
  return RedirectURISchema.parse(await res.json());
}

/**
 * Logs out the user by clearing the guest session (if any) and WorkOS session.
 */
export async function handleLogout(): Promise<void> {
  clearSentryUser();
  clearExternalSession();
  const guestToken = getGuestSessionToken();
  const res = await fetch(routes.ApiLogout, {
    headers: guestToken ? { [guestSessionHeaderName]: guestToken } : undefined,
  });
  clearGuestSessionToken();
  const { redirectUri } = RedirectURISchema.parse(await res.json());
  window.location.href = redirectUri.route;
}

/**
 * Overrides `callback` if the user is not logged in to open the UnauthorizedModal.
 * Otherwise, returns `callback` unchanged.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useAuthGuard<K, T extends (...args: any[]) => K>(
  callback: T
): {
  guardedCallback: T | (() => void);
  loading: boolean;
} {
  const { currentUser, loading } = useCurrentUserQuery();

  /** Not finished authenticating */
  if (loading) return { guardedCallback: doNothing, loading };

  /** Not logged in, put up the unauthorized modal. */
  if (!currentUser) return { guardedCallback: () => setUnauthenticatedModalOpen(true), loading };

  return { guardedCallback: callback, loading };
}

const unauthModalOpenVar = makeVar<boolean>(false);

export function setUnauthenticatedModalOpen(open: boolean): void {
  unauthModalOpenVar(open);
}

export function useIsAuthModalOpen(): boolean {
  return useReactiveVar(unauthModalOpenVar);
}
